import { groupBy } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import {
  ClaimDiagnosisDto,
  DiagnosisSides,
  DischargeClaimArgsDto,
  DischargeClinicalData
} from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  DisplayOutcome,
  DisplayOutcomeDetail,
  SideOfBody
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { DiagnosisSideData } from "@shared-types/clinical/diagnosis-side-data.interface.ts";
import { DischargeStatus } from "@shared-types/clinical/discharge-status.enum.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { ClaimDischarge } from "@stores/acc/models/ClaimDischarge.ts";
import { ClaimEncounter } from "@stores/acc/models/ClaimEncounter.ts";

import { ClaimDischargeFormValues } from "../types/dischargeFormValues.type.ts";

export class DischargeHelper {
  constructor(
    private root: IRootStore,
    public claim: Claim
  ) {}

  private claimDischarges?: ClaimDischarge[];
  private claimEncounters?: ClaimEncounter[];

  get core() {
    return this.root.core;
  }

  get acc() {
    return this.root.acc;
  }

  get clinical() {
    return this.root.clinical;
  }

  getClaimDischargeClinicalData = async () => {
    const [claimDischarges, claimEncounters] = await Promise.all([
      this.acc.getClaimDischarges(this.claim.id),
      this.acc.getClaimEncounters(this.claim.id)
    ]);

    this.claimDischarges = claimDischarges;
    this.claimEncounters = claimEncounters;
  };

  get selectedClaimDischarge(): ClaimEncounter | undefined {
    // Get dischareable encounters
    const dischargeableEncounters = this.getDischargeableEncounters();

    return dischargeableEncounters.length > 0
      ? dischargeableEncounters?.reduce(
          (latest: ClaimEncounter | undefined, claimEncounter) =>
            latest &&
            (latest.visitDateTime ?? 0) > (claimEncounter.visitDateTime ?? 0)
              ? latest
              : claimEncounter
        )
      : undefined;
  }

  get initialValues(): ClaimDischargeFormValues | undefined {
    return {
      dischargeDate: DateTime.jsDateNow(),
      businessRoles: this.disciplines.map(x => x.value)
    };
  }

  get haveMultipleDisciplines(): boolean {
    return this.getGroupedDischargeableEncounters().length > 1;
  }

  get totalConsultCount(): number {
    return this.claimEncounters?.length ?? 0;
  }

  get haveMultipleDischarges(): boolean {
    if (!this.claimEncounters) return false;

    const groupedClaimEncounters = groupBy(
      this.claimEncounters,
      x => x.businessRole
    );
    return groupedClaimEncounters.length > 1;
  }

  getDiagnoses = () => {
    if (!this.claim.claimDiagnosis) {
      return {
        primaryDiagnoses: [{ originalText: "Undiagnosed" }],
        additionalDiagnoses: []
      };
    }

    // Get primary diagnoses
    const primaryDiagnoses = this.claim.primaryDiagnosis
      ? [this.mapToDiagnosisSide(this.claim.primaryDiagnosis)]
      : [{ originalText: "Undiagnosed" }];

    // Get additional diagnoses which are not included the primary diagnosises
    const additionalDiagnoses = this.claim.claimDiagnosis
      .filter(
        x =>
          x.terminology?.code !== this.claim.primaryDiagnosis?.terminology?.code
      )
      .map(x => this.mapToDiagnosisSide(x));

    return {
      primaryDiagnoses,
      additionalDiagnoses
    };
  };

  getDischargeDescription = () => {
    const claimEncounters = this.getGroupedDischargeableEncounters();

    if (claimEncounters && claimEncounters.length > 1) {
      // Get completed disciplines
      const completedDisciplines = this.getCompletedDisciplines();

      if (completedDisciplines.length + 1 < claimEncounters.length) {
        return `${completedDisciplines.length + 1} of ${
          claimEncounters.length
        }`;
      }
    }
    return "Final discharge";
  };

  get disciplines() {
    // Get dischareable encounters
    const dischargeableEncounters = this.getGroupedDischargeableEncounters()
      .filter(x => x[0])
      .map(m => m[0]) as string[];

    return (
      dischargeableEncounters?.map(x => {
        const roledescription = this.core.catalogBusinessRoles.find(
          role => role.code === x
        )?.text;
        return {
          value: x,
          label: roledescription
        };
      }) ?? []
    );
  }

  handleSubmitDischarge = async (values: ClaimDischargeFormValues) => {
    if (!values.businessRoles || values.businessRoles.length === 0) return;

    // Get the discharge outcome detail and comment
    let detail: string[] | undefined;
    let comment: string | undefined;
    switch (values.dischargeOutcome) {
      case DisplayOutcome.PartiallyResolved:
        if (!!values.dischargeOutcomePartiallyResolved)
          detail = values.dischargeOutcomePartiallyResolved;

        if (
          values.dischargeOutcomePartiallyResolved?.find(
            x => x === DisplayOutcomeDetail.SpecialistReferral
          )
        ) {
          comment = values.specialistInformationPartiallyResolved;
        }
        break;
      case DisplayOutcome.Unresolved:
        if (!!values.dischargeOutcomeUnresolved)
          detail = [values.dischargeOutcomeUnresolved];

        if (
          values.dischargeOutcomeUnresolved ===
          DisplayOutcomeDetail.WillBenefitFromSpecialistReferral
        ) {
          comment = values.specialistInformationUnresolved;
        }
        break;
      case DisplayOutcome.Resolved:
        if (!!values.dischargeOutcomeResolved)
          detail = [values.dischargeOutcomeResolved];
    }

    // Create DischargeClinicalData with the outcome detail
    const data: DischargeClinicalData = {
      discharger: values.discharger,
      dischargeDate: DateTime.jsDateToISODate(values.dischargeDate),
      outcomeDetail: {
        outcome: values.dischargeOutcome,
        detail,
        comment
      },
      notes: values.notes
    };

    // Create discharge request
    const request: DischargeClaimArgsDto = {
      claimId: this.claim.id,
      businessRoles: values.businessRoles,
      dischargeClinicalData: data
    };

    await this.acc.dischargeClaim(request);
  };

  handleSubmitSucceeded = () => {
    // Redirect to the claim list
    this.root.routing.push(routes.claims.basePath.pattern);
  };

  private getGroupedDischargeableEncounters() {
    return groupBy(this.getDischargeableEncounters(), x => x.businessRole);
  }

  getDischargeableEncounters(): ClaimEncounter[] {
    const completedBusinessRoles = this.getCompletedDisciplines().map(
      x => x.businessRole
    );

    return (
      this.claimEncounters?.filter(
        x => x.businessRole && !completedBusinessRoles.includes(x.businessRole)
      ) ?? []
    );
  }

  private getCompletedDisciplines(): ClaimDischarge[] {
    return (
      this.claimDischarges?.filter(
        x => x.discharge?.dischargeStatus === DischargeStatus.Completed
      ) ?? []
    );
  }

  private mapToDiagnosisSide(item: ClaimDiagnosisDto): DiagnosisSideData {
    return {
      originalText: item.terminology?.text,
      side: this.getDiagnosisSide(item.diagnosisSide)
    };
  }

  private getDiagnosisSide(value: string | undefined) {
    if (!value) return SideOfBody.Neither;

    switch (value) {
      case DiagnosisSides.Left:
        return SideOfBody.Left;
      case DiagnosisSides.Right:
        return SideOfBody.Right;
      case DiagnosisSides.NotApplicable:
        return SideOfBody.Neither;
      case DiagnosisSides.Bilateral:
        return SideOfBody.Both;
      default:
        return undefined;
    }
  }
}
